// src/hooks/useReduxTheme.js
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectThemeMode } from 'store/selectors/themeSelectors';

/**
 * Custom hook for theme initialization and management
 */
export const useReduxTheme = () => {
    const dispatch = useDispatch();
    const themeMode = useSelector(selectThemeMode);

    useEffect(() => {
        // Apply theme to document on theme change
        document.documentElement.setAttribute('data-theme', themeMode);

        // Update CSS custom properties based on theme
        const root = document.documentElement;

        if (themeMode === 'dark') {
            // Dark mode specific CSS variables
            root.style.setProperty('--bs-body-bg', '#131920');
            root.style.setProperty('--bs-body-color', '#dbe0e5');

            // Additional dark mode variables
            root.style.setProperty('--bs-tertiary-bg', '#1d2630');
            root.style.setProperty('--bs-secondary-bg', '#1d2630');
            root.style.setProperty('--bs-body-bg-rgb', '19, 25, 32');
            root.style.setProperty('--bs-body-color-rgb', '219, 224, 229');

            // Card and surface variables
            root.style.setProperty('--bs-secondary-color-rgb', '139, 150, 164');
            root.style.setProperty('--bs-tertiary-color-rgb', '139, 150, 164');
            root.style.setProperty('--bs-secondary-bg-rgb', '29, 38, 48');
            root.style.setProperty('--bs-tertiary-bg-rgb', '29, 38, 48');

            // Border and shadow variables
            root.style.setProperty('--bs-border-color', '#3e4853');
            root.style.setProperty('--bs-border-color-rgb', '62, 72, 83');
        } else {
            // Light mode (reset to defaults)
            root.style.setProperty('--bs-body-bg', '#ffffff');
            root.style.setProperty('--bs-body-color', '#1d2630');
            root.style.setProperty('--bs-tertiary-bg', '#f8f9fa');
            root.style.setProperty('--bs-secondary-bg', '#f3f5f7');
            root.style.setProperty('--bs-body-bg-rgb', '255, 255, 255');
            root.style.setProperty('--bs-body-color-rgb', '29, 38, 48');
            root.style.setProperty('--bs-secondary-color-rgb', '91, 107, 121');
            root.style.setProperty('--bs-tertiary-color-rgb', '91, 107, 121');
            root.style.setProperty('--bs-secondary-bg-rgb', '243, 245, 247');
            root.style.setProperty('--bs-tertiary-bg-rgb', '248, 249, 250');
            root.style.setProperty('--bs-border-color', '#dbe0e5');
            root.style.setProperty('--bs-border-color-rgb', '219, 224, 229');
        }
    }, [themeMode]);

    return {
        themeMode,
        isDarkMode: themeMode === 'dark',
        isLightMode: themeMode === 'light'
    };
};
